"use client"

import {
  type ConnectedStandardSolanaWallet,
  useSignTransaction,
  useWallets,
} from "@privy-io/react-auth/solana"
import { wrap } from "@reatom/core"
import { useMemo } from "react"
import { requestGachaPortfolioRefresh } from "./gacha-portfolio-refresh"
import {
  createSolanaGachaExecution,
  getPreferredSolanaPurchaseWallet,
} from "./gacha-solana-execution"
import { useGachaApiOptions } from "./use-gacha-api-options"
import { useGachaSolanaConfig } from "./use-gacha-solana-config"
import { requestGachaUsdcBalanceRefresh } from "./use-gacha-usdc-balance"

export function useGachaSolanaExecution({
  purchaseWalletAddress = "",
  purchaseWallet = null,
}: {
  purchaseWalletAddress?: string
  purchaseWallet?: ConnectedStandardSolanaWallet | null
} = {}) {
  const { bffBaseUrl } = useGachaApiOptions()
  const { chain } = useGachaSolanaConfig()
  const { ready, wallets } = useWallets()
  const { signTransaction } = useSignTransaction()
  const wallet = useMemo(() => {
    if (purchaseWallet?.address === purchaseWalletAddress) return purchaseWallet
    const originalWallet = purchaseWalletAddress
      ? wallets.find((wallet) => wallet.address === purchaseWalletAddress)
      : undefined
    return originalWallet ?? getPreferredSolanaPurchaseWallet(wallets)
  }, [purchaseWallet, purchaseWalletAddress, wallets])
  const execution = useMemo(
    () =>
      wallet
        ? createSolanaGachaExecution({
            walletAddress: wallet.address,
            apiOptions: { bffBaseUrl },
            signTransaction: async (transaction) => {
              const { signedTransaction } = await wrap(
                signTransaction({ transaction, wallet, chain }),
              )
              return signedTransaction
            },
            refreshBalance: requestGachaUsdcBalanceRefresh,
            refreshPortfolio: requestGachaPortfolioRefresh,
          })
        : null,
    [bffBaseUrl, chain, signTransaction, wallet],
  )

  return { ready, walletAddress: wallet?.address ?? "", execution }
}
